import { createContext, use, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { NavigationContext } from "lib/navigation/use-navigation";
import { ContentNode } from "lib/types";

import css from "app/footnote.module.scss";

interface ActiveFootnote {
  number: string;
  content: ContentNode;
}

interface FootnotesContextType {
  open(footnote: ActiveFootnote): void;
  close(): void;
}

export const FootnotesContext = createContext<FootnotesContextType | null>(
  null
);
FootnotesContext.displayName = "FootnotesContext";

function extractText(node: ContentNode | string): string {
  if (typeof node === "string") return node;
  return node.c?.map(extractText).join("") ?? "";
}

export function FootnotesProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<{
    page: number;
    footnote: ActiveFootnote;
  } | null>(null);
  const { page } = use(NavigationContext)!;

  const current = active?.page === page ? active.footnote : null;

  function open(footnote: ActiveFootnote) {
    setActive(prev =>
      prev?.footnote.number === footnote.number ? null : { page, footnote }
    );
  }

  function close() {
    setActive(null);
  }

  return (
    <FootnotesContext value={{ open, close }}>
      {children}
      {current &&
        createPortal(
          <div className={css.portal}>
            <div className={css.header}>
              <span>{current.number}</span>
              <button className={css.close} onClick={close}>
                ✕
              </button>
            </div>
            <div className={css.content}>
              <div className={css.footnote}>
                <span className={css.text}>{extractText(current.content)}</span>
              </div>
            </div>
          </div>,
          document.body
        )}
    </FootnotesContext>
  );
}
